import React, { useState } from 'react';
import styled from 'styled-components';
import TabContext from '@mui/lab/TabContext';

import { useSelector } from 'react-redux';
import { selectCommandByNodeId, showTelnyxFeatures, State } from '@store/index';
import { TabList, TabPanel } from '@mui/lab';
import { Tab } from '@mui/material';
import { NodeField, NodeFieldType } from '../../';
import { NodeInputExpression, NodeSidebar, NodeSidebarTab, NodeSidebarTabsContainer } from '@common/Node/NodeSidebar';
import { After, ToolArguments } from '@common/Node/NodeSidebar/Custom';
import { CustomOutput } from '@common/Node/NodeSidebar/Extensions/CustomOutput';
import { ToolExtension } from './Tool';
import { BucketSearch } from '@common/Node/NodeSidebar/Extensions/BucketSearch';
import { APISearch } from '@common/Node/NodeSidebar/Extensions/APISearch';
import { PricingCalculator } from '@common/Node/NodeSidebar/Extensions/PricingCalculator';
import { SettingsOutlined } from '@mui/icons-material';
import { HTTPRequest } from '@common/Node/NodeSidebar/Extensions/HTTPRequest/HTTPRequest';

const ExtensionContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  gap: 1rem;
`;

export const ToolTabs: React.FC = () => {
  const [tab, setTab] = useState('1');

  const isTelnyxUser = useSelector((state: State) => showTelnyxFeatures(state));
  const themeType = useSelector((state: State) => state.flow.theme);

  const command = useSelector((state: State) => selectCommandByNodeId(state, state.tabs.active.selectedNode));
  if (!command) return;

  const fields = command.fields;
  const background = themeType === 'dark' ? 'rgb(88, 88, 255)' : command.config.background;
  const inputFields = fields.filter((field) => field.tab === '1');
  const toolArgumentsField = fields.find((field) => field.type === NodeFieldType.ToolArgs);
  const extensionFields = fields.filter((field) => field.tab === '3');
  const afterField = fields.find((field) => field.type === NodeFieldType.After);

  const extensionEnabled = extensionFields.map((field) => command.state[field.id].enabled).some(Boolean);

  const renderField = (field: NodeField) => {
    const { type } = field;

    if (type === ToolExtension.HTTPRequest) return <HTTPRequest key={field.id} command={command} field={field} />;
    if (type === ToolExtension.PricingCalculator && isTelnyxUser)
      return <PricingCalculator key={field.id} command={command} field={field} />;
    if (type === ToolExtension.APISearch && isTelnyxUser)
      return <APISearch key={field.id} command={command} field={field} />;
    if (type === ToolExtension.BucketSearch) return <BucketSearch key={field.id} command={command} field={field} />;
    if (type === ToolExtension.CustomOutput) return <CustomOutput key={field.id} command={command} field={field} />;
  };

  return (
    <NodeSidebar>
      <TabContext value={tab}>
        <NodeSidebarTabsContainer color={background}>
          <TabList onChange={(_, newTab) => setTab(newTab)}>
            <Tab disableRipple label='Node' value='1' />
            <Tab disableRipple label='Arguments' value='2' />
            <Tab disableRipple label='Output' value='3' />
            <Tab disableRipple iconPosition='start' icon={<SettingsOutlined />} value='4' />
          </TabList>
        </NodeSidebarTabsContainer>
        <TabPanel value='1'>
          <NodeSidebarTab>
            {inputFields.map((field: NodeField) => (
              <NodeInputExpression key={field.id} field={field} command={command} />
            ))}
          </NodeSidebarTab>
        </TabPanel>
        <TabPanel value='2'>
          <NodeSidebarTab>
            {toolArgumentsField && <ToolArguments command={command} field={toolArgumentsField} />}
          </NodeSidebarTab>
        </TabPanel>
        <TabPanel value='3'>
          <ExtensionContainer>
            {extensionFields.map((field) => {
              const enabledField = command.state[field.id].enabled;
              if (enabledField) return renderField(field);
              if (extensionEnabled) return null;

              return renderField(field);
            })}
          </ExtensionContainer>
        </TabPanel>
        <TabPanel value='4'>{afterField && <After command={command} field={afterField} />}</TabPanel>
      </TabContext>
    </NodeSidebar>
  );
};
